import React, { useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, CheckCircle, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const PartnerRequest = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!fullName || !email) {
      toast({
        title: "Missing Information",
        description: "Please enter your full name and email address.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert the signup request into the database
      const { error: insertError } = await supabase
        .from('pending_partner_signups')
        .insert({
          full_name: fullName,
          email: email,
          message: message || null,
        });

      if (insertError) {
        // Check for duplicate email
        if (insertError.code === '23505') {
          toast({
            title: "Request Already Exists",
            description: "A request with this email has already been submitted. We'll be in touch soon.",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
        throw insertError;
      }

      // Send notification email to admin
      const { error: notifyError } = await supabase.functions.invoke('notify-partner-request', {
        body: {
          fullName,
          email,
          message,
        },
      });

      if (notifyError) {
        console.error('Error sending notification:', notifyError);
        // Don't fail the submission if notification fails - request is already saved
      }

      setIsSubmitted(true);
      toast({
        title: "Request Submitted",
        description: "Thank you for your interest. We'll review your request and get back to you soon.",
      });
    } catch (error: any) {
      console.error('Error submitting request:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <PageLayout>
        <main className="pt-20 flex-grow">
          <div className="container mx-auto px-4 py-12">
            <div className="max-w-lg mx-auto">
              <Card className="border border-delphi-gray/20 bg-delphi-dark/80 backdrop-blur-sm shadow-lg text-center">
                <CardHeader className="space-y-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <CardTitle className="text-2xl text-delphi-light">Request Submitted</CardTitle>
                  <CardDescription className="text-delphi-light/70">
                    Thank you for your interest in becoming a DEL(PHI) partner.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-delphi-light/80">
                    We've received your request and will review it shortly. If approved, you'll receive 
                    an email with your login credentials.
                  </p>
                  <p className="text-delphi-light/60 text-sm">
                    This typically takes 1-2 business days.
                  </p>
                </CardContent>
                <CardFooter className="justify-center">
                  <Button 
                    variant="outline"
                    className="text-delphi-light border-delphi-gray/40 hover:bg-delphi-gray/20"
                    onClick={() => window.location.href = '/'}
                  >
                    Return Home
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </main>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <main className="pt-20 flex-grow">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-lg mx-auto">
            <Card className="border border-delphi-gray/20 bg-delphi-dark/80 backdrop-blur-sm shadow-lg">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-delphi-light">Become a Partner</CardTitle>
                <CardDescription className="text-delphi-light/70">
                  Submit your request to join DEL(PHI) as an investment partner. 
                  Your application will be reviewed and you'll receive login credentials upon approval.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="fullName" className="text-sm font-medium text-delphi-light">
                      Full Name *
                    </label>
                    <Input
                      id="fullName"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-delphi-light">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-delphi-light">
                      Message (Optional)
                    </label>
                    <Textarea
                      id="message"
                      placeholder="Tell us a bit about yourself and your investment interests..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="bg-delphi-dark text-delphi-light border-delphi-gray/40 min-h-[100px]"
                      disabled={isSubmitting}
                    />
                  </div>
                </form>
              </CardContent>
              <CardFooter className="flex-col space-y-4">
                <Button 
                  className="w-full bg-delphi-red hover:bg-red-600"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <Send size={16} className="ml-2" />
                    </>
                  )}
                </Button>
                <p className="text-center text-delphi-light/60 text-sm">
                  Already have an account? <a href="/partner-login" className="text-delphi-red hover:underline">Sign in here</a>
                </p>
              </CardFooter>
            </Card>
          </div>
        </div>
      </main>
    </PageLayout>
  );
};

export default PartnerRequest;
